import React, { useState } from 'react'
import { Message } from 'semantic-ui-react'
import { FlexRow } from 'components'
import { AppPrimaryButton, AppSecondaryButton } from 'elements'
import {
    SIGNATURE_TYPE,
    buildApprovalPayload,
    formatApprovalDate,
} from './report-approval-utils'
import { ApprovalHistory } from './ApprovalHistory'
import {
    SignatureCapture,
    validateSignatureCapture,
} from './SignatureCapture'
import {
    ApprovalStatusBadge,
    InfoCard,
    LandingGrid,
    ProgressTrack,
    SectionCard,
} from './report-approval-styles'

export const ApproverLandingView = ({
    report,
    history = [],
    historyLoading = false,
    currentUserName = '',
    canReject = true,
    linkExpiresOn = null,
    onApprove,
    onReject,
    submitting = false,
    error = '',
}) => {
    const [activeTab, setActiveTab] = useState(SIGNATURE_TYPE.Typed)
    const [notes, setNotes] = useState('')
    const [drawnSignature, setDrawnSignature] = useState('')
    const [uploadedSignature, setUploadedSignature] = useState('')
    const [typedName, setTypedName] = useState(currentUserName || '')
    const [validationError, setValidationError] = useState('')

    const currentStep = Number(report?.currentStep || 1)
    const totalSteps = Number(report?.totalSteps || history.length || 1)
    const pct = totalSteps ? Math.round((currentStep / totalSteps) * 100) : 33
    const nextName = report?.nextApproverName
    const auditorDisplay =
        report?.auditorName || report?.auditorEmail || 'the auditor'
    const auditName = report?.auditName || report?.templateName || 'Report'

    const handleApprove = () => {
        const signatureError = validateSignatureCapture({
            activeTab,
            drawnSignature,
            uploadedSignature,
            typedName,
        })
        if (signatureError) {
            setValidationError(signatureError)
            return
        }
        setValidationError('')
        onApprove(
            buildApprovalPayload({
                notes,
                signatureType: activeTab,
                signatureBase64:
                    activeTab === SIGNATURE_TYPE.Draw
                        ? drawnSignature
                        : uploadedSignature,
                signatureText: typedName,
                notifyAuditor: false,
            }),
        )
    }

    return (
        <>
            <FlexRow className="justify-content-between align-items-start mb-3">
                <div>
                    <h1 style={{ margin: '0 0 4px', fontSize: 22 }}>
                        Sign &amp; approve report
                    </h1>
                    <p style={{ margin: 0, color: '#6b7280', fontSize: 13 }}>
                        Approving as{' '}
                        <strong>{currentUserName || 'Approver'}</strong>
                        {totalSteps
                            ? ` · Step ${currentStep} of ${totalSteps} · Waiting on you`
                            : ''}
                    </p>
                    {linkExpiresOn && (
                        <p style={{ margin: '2px 0 0', color: '#6b7280', fontSize: 12 }}>
                            This link works once and expires on{' '}
                            {formatApprovalDate(linkExpiresOn)}.
                        </p>
                    )}
                </div>
                <ApprovalStatusBadge $status="awaiting_me">
                    PENDING YOUR REVIEW
                </ApprovalStatusBadge>
            </FlexRow>

            <ProgressTrack $pct={pct}>
                <div />
            </ProgressTrack>
            <p
                style={{
                    textAlign: 'right',
                    fontSize: 12,
                    color: '#6b7280',
                    marginBottom: 16,
                }}
            >
                Approver {currentStep} of {totalSteps || 1}
            </p>

            {(validationError || error) && (
                <Message negative size="small">
                    {validationError || error}
                </Message>
            )}

            <LandingGrid>
                <div>
                    <SectionCard>
                        <h4
                            style={{
                                margin: '0 0 12px',
                                color: '#2563eb',
                                fontSize: 16,
                            }}
                        >
                            Report summary
                        </h4>
                        <InfoCard style={{ marginBottom: 0 }}>
                            <div className="meta-row">
                                <label>Audit name</label>
                                <div className="value">
                                    <strong>{auditName}</strong>
                                </div>
                            </div>
                            <div className="meta-row">
                                <label>Template</label>
                                <div className="value">
                                    {report?.templateName || '—'}
                                </div>
                            </div>
                            {report?.locationName && (
                                <div className="meta-row">
                                    <label>Location</label>
                                    <div className="value">
                                        {report.locationName}
                                    </div>
                                </div>
                            )}
                            <div className="meta-row">
                                <label>Auditor</label>
                                <div className="value">{auditorDisplay}</div>
                            </div>
                            <div className="meta-row">
                                <label>Completed</label>
                                <div className="value">
                                    {formatApprovalDate(report?.completedAt)}
                                </div>
                            </div>
                            {report?.score != null && (
                                <div className="meta-row">
                                    <label>Score</label>
                                    <div className="value">
                                        {typeof report.score === 'number'
                                            ? `${report.score}%`
                                            : report.score}
                                        {report.questionsPassed != null &&
                                            report.questionsTotal != null &&
                                            ` · ${report.questionsPassed}/${report.questionsTotal} questions passed`}
                                    </div>
                                </div>
                            )}
                        </InfoCard>
                        <div style={{ marginTop: 14 }}>
                            {report?.pdfUrl && (
                                <AppSecondaryButton
                                    type="button"
                                    onClick={() =>
                                        window.open(
                                            report.pdfUrl,
                                            '_blank',
                                            'noopener',
                                        )
                                    }
                                >
                                    View full PDF
                                </AppSecondaryButton>
                            )}
                        </div>
                    </SectionCard>

                    <div className="mt-3">
                        <ApprovalHistory
                            events={history}
                            loading={historyLoading}
                            title="Approval history so far"
                        />
                    </div>
                </div>

                <div>
                    <SectionCard>
                        <h4
                            style={{
                                margin: '0 0 12px',
                                color: '#2563eb',
                                fontSize: 16,
                            }}
                        >
                            Your signature
                        </h4>
                        <SignatureCapture
                            activeTab={activeTab}
                            onTabChange={setActiveTab}
                            drawnSignature={drawnSignature}
                            onDrawnChange={setDrawnSignature}
                            uploadedSignature={uploadedSignature}
                            onUploadedChange={setUploadedSignature}
                            typedName={typedName}
                            onTypedChange={setTypedName}
                            onValidationError={setValidationError}
                        />
                        <p style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>
                            By signing, you confirm you have reviewed the report
                            and take responsibility for this approval step.
                        </p>
                        <label
                            style={{
                                display: 'block',
                                fontWeight: 600,
                                fontSize: 13,
                                margin: '14px 0 6px',
                            }}
                        >
                            Notes for the next approver (optional)
                        </label>
                        <textarea
                            rows={3}
                            value={notes}
                            onChange={e => setNotes(e.target.value)}
                            placeholder="Any concerns or context..."
                            style={{
                                width: '100%',
                                padding: '8px 10px',
                                border: '1px solid #d1d5db',
                                borderRadius: 6,
                                fontSize: 13,
                            }}
                        />
                        <div
                            style={{
                                display: 'flex',
                                gap: 8,
                                marginTop: 14,
                            }}
                        >
                            <AppPrimaryButton
                                type="button"
                                onClick={handleApprove}
                                disabled={submitting}
                                style={{ flex: 1, background: '#16a34a' }}
                            >
                                {submitting && 'Please wait…'}
                                {!submitting &&
                                    nextName &&
                                    `Approve & send to ${nextName.split(' ')[0]}`}
                                {!submitting && !nextName && 'Approve'}
                            </AppPrimaryButton>
                            {canReject && (
                                <AppSecondaryButton
                                    type="button"
                                    onClick={onReject}
                                    disabled={submitting}
                                    style={{ color: '#dc2626', borderColor: '#dc2626' }}
                                >
                                    Reject
                                </AppSecondaryButton>
                            )}
                        </div>
                    </SectionCard>
                    <SectionCard
                        style={{
                            marginTop: 12,
                            background: '#eef2ff',
                            borderColor: '#2563eb',
                        }}
                    >
                        <div style={{ fontSize: 12, color: '#1e3a8a' }}>
                            <strong>Please do not forward this link.</strong>{' '}
                            Anyone who has it can approve or reject this step in
                            your name. To hand the approval to someone else,
                            ask the template admin to change the approver.
                        </div>
                    </SectionCard>
                </div>
            </LandingGrid>
        </>
    )
}
